import React, { useContext } from "react";
import CardApplicationAPI from "../../api/commercial/card-application";
import {
  ApplicationFlows,
  ApplicationFlowSteps,
} from "../../route/application/authenticated/application-flow/types";
import { CardholderModel } from "../cardholder-basic-information/cardholder-form/types";
import CardholderBasicMapper from "../cardholder-basic-information/mapper";
import { RejectionReason } from "./types";
import Confirmation from "./index";
import { LanguageContext } from "../../context/languageContext";
import {
  BasicAccountApplicationDocument,
  CardApplicationDocument,
  CompanyAttachment,
  PersonAttachment,
  SigningProcessDocument,
} from "../../api/commercial/generated-types";
import { ProductRefType } from "../../context/product-context/types";
import ApplicationAPI from "../../api/commercial/application";
import { ErrorContext } from "../../route/error/provider";
import { useFeatureToggles } from "../shared/hooks/useFeatureToggles";
import { Feature } from "../../context/types";

export default function ConfirmationAdapter({
  flowType,
  basicAccountApplication,
  cardApplication,
  signingProcess,
  setStep,
  triggerReload,
}: {
  basicAccountApplication: BasicAccountApplicationDocument;
  flowType: ApplicationFlows;
  cardApplication: CardApplicationDocument;
  signingProcess: SigningProcessDocument;
  setStep: (step: ApplicationFlowSteps) => void;
  triggerReload: () => void;
}): React.JSX.Element {
  const { setError } = useContext(ErrorContext);
  const { language } = useContext(LanguageContext);

  const basicApplicationRef = cardApplication.basicAccountApplicationRef;
  const cardApplicationRef = cardApplication.applicationRef;
  const selectedProductRef = cardApplication.productRef as ProductRefType;

  const company = cardApplication.company;

  const cardholder: CardholderModel =
    CardholderBasicMapper.mapCardholderDocumentToPageModel(
      cardApplication,
      language,
    );
  const canDigitallySign = cardApplication.canDigitallySign;
  const status = cardApplication.status;
  const requiredAttachmentsCA: PersonAttachment[] =
    cardApplication.requiredAttachments;

  let rejectionReason: RejectionReason;
  if (signingProcess) {
    if (signingProcess.isIdentificationRejected) {
      rejectionReason = (
        signingProcess.identificationRejectionReason
          ? signingProcess.identificationRejectionReason
          : RejectionReason.IDENTIFICATION_REJECTED
      ) as RejectionReason;
    } else if (signingProcess.isSigningRejected) {
      rejectionReason = RejectionReason.SIGNING_REJECTED;
    }
  }

  let requiredCompanyAttachmentsBAA: CompanyAttachment[];
  let requiredPersonAttachmentsBAA: PersonAttachment[];
  if (basicAccountApplication) {
    requiredCompanyAttachmentsBAA =
      basicAccountApplication.requiredCompanyAttachments;
    requiredPersonAttachmentsBAA =
      basicAccountApplication.requiredPersonAttachments;
  }

  const canUploadAttachments = useFeatureToggles(Feature.ATTACHMENT_UPLOAD);

  return (
    <Confirmation
      flowType={flowType}
      context={{
        cardholder,
        companyName: company.name,
        status,
        rejectionReason,
        canDigitallySign,
        selectedProductRef,
        basicApplicationRef,
        cardApplicationRef,
        requiredCompanyAttachmentsBAA,
        requiredPersonAttachmentsBAA,
        requiredAttachmentsCA,
      }}
      previous={() => setStep(ApplicationFlowSteps.CHOOSE_SIGNATORIES)}
      next={async (values): Promise<void> => {
        if (canDigitallySign && values.wantsToDigitallySign === "yes") {
          const url = await CardApplicationAPI.signFirstCardApplication(
            basicApplicationRef,
            cardApplicationRef,
            language,
          );
          window.location.replace(url);
          return;
        }

        try {
          await ApplicationAPI.manuallySignApplication(
            cardApplication.applicationRef,
          );
        } catch (e) {
          setError(e);
        }

        if (canUploadAttachments) {
          setStep(ApplicationFlowSteps.UPLOAD_ATTACHMENTS);
        } else {
          triggerReload();
        }
      }}
    />
  );
}
